import { isApiError } from "@colorglo/contracts/client";
import {
  Alert,
  AlertTitle,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
  Separator,
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "@colorglo/ui/components";
import { ActionImpersonate, iconSize } from "@colorglo/ui/icons";
import { createRootRoute, Outlet, useLocation, useNavigate } from "@tanstack/react-router";
import { Fragment, useEffect } from "react";
import { useMe } from "../api/hooks.ts";
import { AppSidebar } from "../components/app-sidebar.tsx";
import { NotificationBell } from "../components/notifications/bell.tsx";
import { CartButton } from "../components/shop/cart-button.tsx";
import { HqBell } from "../components/shop/hq-bell.tsx";
import { useFranchise } from "../lib/franchise.ts";
import { useT } from "../lib/locale.ts";
import { crumbsFor } from "../lib/nav.ts";

export const Route = createRootRoute({
  component: RootLayout,
});

/**
 * Routes a signed-out visitor can reach. Everything else needs a session, and the app has
 * no useful chrome to draw without one — `GET /v1/me` is what names the franchise every
 * other request is scoped to.
 */
const PUBLIC_PATHS = ["/logga-in", "/inbjudan", "/aterstall-losenord"];

/**
 * Send an unauthenticated visitor to the sign-in page.
 *
 * Keyed on the error *code* rather than on "no data": a network failure or a 500 must land
 * on the calendar's own error state, not silently log someone out. `isFinalAnswer` in
 * `lib/query.ts` already stops `UNAUTHENTICATED` being retried, so this fires once.
 */
function useRequireSession(isPublic: boolean) {
  const me = useMe();
  const navigate = useNavigate();

  const expired = me.isError && isApiError(me.error) && me.error.error.code === "UNAUTHENTICATED";

  useEffect(() => {
    if (expired && !isPublic) void navigate({ to: "/logga-in" });
  }, [expired, isPublic, navigate]);
}

function RootLayout() {
  const { pathname } = useLocation();
  const isPublic = PUBLIC_PATHS.some((path) => pathname.startsWith(path));

  useRequireSession(isPublic);

  if (isPublic) return <Outlet />;

  return <AppShell pathname={pathname} />;
}

function AppShell({ pathname }: { readonly pathname: string }) {
  const t = useT();
  const { franchise, franchiseId, select } = useFranchise();
  const me = useMe();
  const crumbs = crumbsFor(pathname);
  // HQ and country managers do not work the counter — they read the network dashboard and
  // reach a workshop's day by impersonating. The permission, not the role, decides.
  const canSeeBell = franchise?.permissions.includes("staff_notification:read") ?? false;
  const canOrder = franchise?.permissions.includes("shop:order") ?? false;
  const canManageOrders = (me.data?.network_permissions ?? []).includes("shop_order:manage");

  return (
    <SidebarProvider>
      <AppSidebar franchiseId={franchiseId} onFranchiseChange={select} />

      <SidebarInset className="min-h-0 min-w-0">
        {/* Translucent rather than opaque: the calendar scrolls under it, and a hard edge with
            nothing behind it makes the header look detached from the grid it belongs to. */}
        <header className="sticky top-0 z-20 flex h-14 shrink-0 items-center gap-2 border-b border-border bg-background/80 px-4 backdrop-blur-xl">
          <SidebarTrigger aria-label={t("nav.toggle_menu")} />
          <Separator orientation="vertical" className="mr-1 h-5" />

          {/*
           * Which workshop, then which screen. The name alone was the whole header, which
           * repeated what the sidebar's workspace tile already said and answered nothing about
           * where you were — and on the rail, or inside a settings screen with no heading of
           * its own, "where am I" had no answer on the page at all.
           *
           * The workshop drops below `sm`: on a phone the sidebar is a sheet, the trail would
           * wrap to two lines, and the section is the half worth keeping.
           */}
          <Breadcrumb className="min-w-0">
            <BreadcrumbList className="flex-nowrap">
              <BreadcrumbItem className="hidden min-w-0 sm:flex">
                <span className="truncate">{franchise?.name ?? t("app.title")}</span>
              </BreadcrumbItem>
              {crumbs.map((key, index) => (
                <Fragment key={key}>
                  <BreadcrumbSeparator className={index === 0 ? "hidden sm:block" : ""} />
                  <BreadcrumbItem className="min-w-0">
                    {index === crumbs.length - 1 ? (
                      <BreadcrumbPage className="truncate font-medium">{t(key)}</BreadcrumbPage>
                    ) : (
                      <span className="truncate">{t(key)}</span>
                    )}
                  </BreadcrumbItem>
                </Fragment>
              ))}
            </BreadcrumbList>
          </Breadcrumb>

          <div className="ml-auto flex items-center gap-1">
            {canOrder && <CartButton franchiseId={franchiseId} />}
            <HqBell enabled={canManageOrders} />
            <NotificationBell franchiseId={franchiseId} enabled={canSeeBell} />
          </div>
        </header>

        <ImpersonationBanner />

        {/* `min-h-0` so the calendar's own scroll container is the one that scrolls, not this. */}
        <main className="flex min-h-0 flex-1 flex-col">
          <Outlet />
        </main>
      </SidebarInset>
    </SidebarProvider>
  );
}

/**
 * "Am I impersonating?" — loudly, at the top of every page.
 *
 * `GET /v1/me` returns this rather than the client inferring it from a cookie, precisely so
 * the question has one answer and it is the server's. Every write made while this banner is
 * up carries both actors into `audit_log`, and a super_admin who forgets they are inside
 * someone else's franchise is how a booking gets cancelled by the wrong person.
 */
function ImpersonationBanner() {
  const me = useMe();
  const t = useT();
  if (me.data?.impersonation == null) return null;

  return (
    <Alert
      role="status"
      className="shrink-0 rounded-none border-x-0 border-t-0 border-warning bg-warning-subtle text-warning-on"
    >
      <ActionImpersonate {...iconSize.control} aria-hidden="true" />
      <AlertTitle>{t("nav.impersonating", { email: me.data.user.email })}</AlertTitle>
    </Alert>
  );
}
